import type {
  TuituiInteractiveCallbackField,
  TuituiInteractiveCallbackMessage,
  TuituiInteractiveAction,
  TuituiInteractiveFieldItem,
  TuituiOutboundInteractiveMessage,
} from '@qihoo/tuitui-bot-sdk';
import { CHANNEL_ID } from './const';
import { StringDeduplicator } from './deduplicator';
import { modifyInteractiveTuituiMsg, sendTextMsg } from './outbound';
import type { ChatType } from './chat_base';
import type { InboundAccount } from './inbound';

const CARD_INTERACTION_VERSION = 'oct1';
const cardActionDedup = new StringDeduplicator(1000, 900);

export type CardBody = TuituiOutboundInteractiveMessage;
type CardInteractionKind = 'button' | 'quick' | 'form';
type CardInteractionReason = 'malformed' | 'stale' | 'wrong_user' | 'wrong_conversation';
type CardInteractionMetadata = Record<string, string | number | boolean | null | undefined>;

interface CardInteractionEnvelope {
  oc: typeof CARD_INTERACTION_VERSION;
  k: CardInteractionKind;
  a: string;
  q?: string;
  m?: CardInteractionMetadata;
  c?: {
    u?: string;
    h?: string;
    e?: number;
    t?: 'direct' | 'group';
    title?: string;
  };
}

interface ParsedCardAction {
  command: string;
  action: string;
  fields?: { byId: Record<string, string>; byLabel: Record<string, string> };
  title?: string;
}

type ParseCardActionResult =
  | ParsedCardAction
  | { error: CardInteractionReason; message: string }
  | null;

const reasonText: Readonly<Record<CardInteractionReason, string>> = Object.freeze({
  stale: '此操作已过期，请重新发起。',
  wrong_user: '此操作不属于当前用户。',
  wrong_conversation: '此操作不属于当前会话。',
  malformed: '无效的操作数据。',
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isInteractionKind(value: unknown): value is CardInteractionKind {
  return value === 'button' || value === 'quick' || value === 'form';
}

function createInteractionEnvelope(
  value: Omit<CardInteractionEnvelope, 'oc'>,
): CardInteractionEnvelope {
  return { oc: CARD_INTERACTION_VERSION, ...value };
}

function decodeCardAction(params: {
  actionValue: unknown;
  operatorAccount?: string;
  chatId?: string;
  now?: number;
}): CardInteractionEnvelope | CardInteractionReason {
  const value = params.actionValue;
  if (
    !isRecord(value)
    || value.oc !== CARD_INTERACTION_VERSION
    || !isInteractionKind(value.k)
    || typeof value.a !== 'string'
    || !value.a
  ) {
    return 'malformed';
  }
  if (value.q !== undefined && typeof value.q !== 'string') return 'malformed';
  if (
    value.m !== undefined
    && (!isRecord(value.m) || Object.values(value.m).some((item) => {
      return item !== null
        && item !== undefined
        && !['string', 'number', 'boolean'].includes(typeof item);
    }))
  ) {
    return 'malformed';
  }
  if (value.c !== undefined) {
    if (!isRecord(value.c)) return 'malformed';
    const context = value.c;
    if (
      (context.u !== undefined && typeof context.u !== 'string')
      || (context.h !== undefined && typeof context.h !== 'string')
      || (context.e !== undefined && !Number.isFinite(context.e))
      || (context.t !== undefined && context.t !== 'direct' && context.t !== 'group')
      || (context.title !== undefined && typeof context.title !== 'string')
    ) {
      return 'malformed';
    }
    if (typeof context.e === 'number' && context.e < (params.now ?? Date.now())) return 'stale';
    if (typeof context.u === 'string' && context.u.trim() && context.u.trim() !== params.operatorAccount?.trim()) {
      return 'wrong_user';
    }
    if (typeof context.h === 'string' && context.h.trim() && context.h.trim() !== params.chatId?.trim()) {
      return 'wrong_conversation';
    }
  }
  return value as unknown as CardInteractionEnvelope;
}

function extractFieldsData(fields?: TuituiInteractiveCallbackField[]) {
  if (!fields?.length) return undefined;
  const byId: Record<string, string> = {};
  const byLabel: Record<string, string> = {};
  for (const field of fields) {
    const raw = field.input?.combox ?? field.input?.text;
    if (raw === undefined || raw === '') continue;
    const id = field.input?.id || field.name || '';
    const label = field.name || field.input?.id || '';
    if (id) byId[id] = String(raw);
    if (label) byLabel[label] = String(raw);
  }
  return Object.keys(byId).length || Object.keys(byLabel).length ? { byId, byLabel } : undefined;
}

function parseCardAction(params: {
  message: TuituiInteractiveCallbackMessage;
  operatorAccount: string;
  chatId: string;
  deduplicationScope: string;
}): ParseCardActionResult {
  if (params.message.msgid) {
    const key = `${params.deduplicationScope}:${params.message.msgid}`;
    if (!cardActionDedup.checkAndRecord(key)) return null;
  }

  const decoded = decodeCardAction({
    actionValue: params.message.action?.[0]?.value,
    operatorAccount: params.operatorAccount,
    chatId: params.chatId,
  });
  if (typeof decoded === 'string') {
    return { error: decoded, message: reasonText[decoded] };
  }

  const fields = extractFieldsData(params.message.fields);
  const title = decoded.c?.title;
  const metadataText = decoded.m && Object.keys(decoded.m).length
    ? `: ${JSON.stringify(decoded.m)}`
    : '';
  const actionText = decoded.q
    ? decoded.q
    : `[card_action] ${decoded.a}${metadataText}`;
  const answer = fields ? JSON.stringify(fields.byLabel) : actionText;
  const command = title
    ? `Q: ${title}\nA: ${answer}`
    : `${actionText}${fields ? `\n[form_data] ${JSON.stringify(fields.byId)}` : ''}`;
  return {
    command,
    action: decoded.a,
    ...(fields ? { fields } : {}),
    ...(title ? { title } : {}),
  };
}

function wrapAction(action: TuituiInteractiveAction, title?: string): TuituiInteractiveAction {
  if (isRecord(action.value) && action.value.oc === CARD_INTERACTION_VERSION) return action;

  const context = title ? { c: { title } } : {};
  if (typeof action.value === 'string') {
    return {
      ...action,
      value: createInteractionEnvelope({
        k: 'button',
        a: action.name || action.value,
        q: action.value,
        ...context,
      }) as unknown as Record<string, unknown>,
    };
  }
  if (isRecord(action.value)) {
    return {
      ...action,
      value: createInteractionEnvelope({
        k: 'button',
        a: action.name || 'action',
        m: action.value as CardInteractionMetadata,
        ...context,
      }) as unknown as Record<string, unknown>,
    };
  }
  return {
    ...action,
    value: createInteractionEnvelope({
      k: 'button',
      a: action.name || 'action',
      ...context,
    }) as unknown as Record<string, unknown>,
  };
}

export function buildTuituiInteractiveMessage(card: CardBody): TuituiOutboundInteractiveMessage {
  const title = card.head?.text;
  return {
    ...card,
    ...(card.summary || !title ? {} : { summary: title }),
    ...(card.action ? { action: card.action.map((action) => wrapAction(action, title)) } : {}),
  };
}

export function buildConfirmCard(options: {
  title: string;
  content?: string;
  confirmText?: string;
  cancelText?: string;
  doubleCheck?: boolean;
}): CardBody {
  const confirmText = options.confirmText ?? '确认';
  const confirm: TuituiInteractiveAction = {
    text: confirmText,
    name: 'confirm',
    value: confirmText,
    bgcolor: '3873FA',
    color: 'FFFFFF',
  };
  if (options.doubleCheck) {
    confirm.confirm = {
      title: '请再次确认',
      content: options.content ?? `确定要「${confirmText}」吗？`,
    };
  }
  return {
    head: { text: options.title },
    ...(options.content ? { body: { content: options.content } } : {}),
    action: [
      confirm,
      {
        text: options.cancelText ?? '取消',
        name: 'cancel',
        value: options.cancelText ?? '取消',
        bgcolor: 'FA5151',
        color: 'FFFFFF',
      },
    ],
  };
}

export function buildSelectCard(options: {
  title: string;
  options: Array<{ label: string; description?: string }>;
}): CardBody {
  if (!options.options.length) throw new Error('select 卡片至少需要 1 个选项');
  const choices = options.options.slice(0, 6);
  const lines = choices
    .filter((item) => item.description)
    .map((item) => `${item.label} - ${item.description}`);
  if (options.options.length > 6) lines.push('仅显示前 6 个选项');
  return {
    head: { text: options.title },
    ...(lines.length ? { body: { content: lines.join('\n') } } : {}),
    action: choices.map((item) => ({
      text: item.label,
      name: item.label,
      value: item.label,
      bgcolor: '3873FA',
      color: 'FFFFFF',
    })),
  };
}

function submitAction(): TuituiInteractiveAction {
  return { text: '提交', name: 'submit', value: 'submit', bgcolor: '3873FA', color: 'FFFFFF' };
}

export function buildMultiInputCard(options: {
  title: string;
  fields: Array<{ label: string; id: string; required?: boolean; placeholder?: string }>;
}): CardBody {
  const fields: TuituiInteractiveFieldItem[] = options.fields.map((field) => ({
    name: field.label,
    input: {
      id: field.id,
      type: 'text',
      childtype: 0,
      must: field.required ?? false,
      ...(field.placeholder ? { hint: field.placeholder } : {}),
    },
  }));
  return { head: { text: options.title }, fields, action: [submitAction()] };
}

export function buildFormCard(options: {
  title: string;
  fields: Array<{
    label: string;
    id: string;
    type: 'text' | 'combox';
    options?: string[];
    required?: boolean;
    placeholder?: string;
  }>;
}): CardBody {
  const fields: TuituiInteractiveFieldItem[] = options.fields.map((field) => {
    if (field.type === 'combox') {
      if (!field.options?.length) throw new Error(`combox 字段 "${field.label}" 需要至少 1 个 options`);
      return {
        name: field.label,
        input: {
          id: field.id,
          type: 'combox',
          must: field.required ?? false,
          list: field.options.map((item) => ({ key: item, val: item })),
          ...(field.placeholder ? { hint: field.placeholder } : {}),
        },
      };
    }
    return {
      name: field.label,
      input: {
        id: field.id,
        type: 'text',
        childtype: 0,
        must: field.required ?? false,
        ...(field.placeholder ? { hint: field.placeholder } : {}),
      },
    };
  });
  return { head: { text: options.title }, fields, action: [submitAction()] };
}

function buildStatusCard(options: {
  originalTitle?: string;
  submittedText?: string;
  fieldsData?: Record<string, string>;
  operatorName?: string;
}): CardBody {
  const lines = options.submittedText
    ? [`提交内容: ${options.submittedText}`]
    : ['已提交'];
  for (const [key, value] of Object.entries(options.fieldsData ?? {})) {
    if (lines.length === 1) lines.push('');
    lines.push(`${key}: ${value}`);
  }
  return {
    head: { text: options.originalTitle ?? '已提交', bgcolor: '#3873FA', tcolor: '#FFFFFF' },
    body: { content: lines.join('\n') },
    footer: [{
      ...(options.operatorName ? { text: options.operatorName } : {}),
      ts: Math.floor(Date.now() / 1000),
    }],
  };
}

export async function handleCardAction(params: {
  message: TuituiInteractiveCallbackMessage;
  operatorAccount: string;
  chatId: string;
  chatType: ChatType;
  account: InboundAccount;
  log: any;
}): Promise<string | null> {
  const { message, operatorAccount, chatId, chatType, account, log } = params;
  const { accountId } = account;
  const result = parseCardAction({
    message,
    operatorAccount,
    chatId,
    deduplicationScope: accountId,
  });

  if (!result) return null;
  if ('error' in result) {
    log?.warn?.(`[${CHANNEL_ID}] AccountId: ${accountId}, rejected card action: ${result.error}, operator=${operatorAccount}`);
    try {
      await sendTextMsg(account, chatId, chatType, result.message);
    } catch (err) {
      log?.warn?.(`[${CHANNEL_ID}] AccountId: ${accountId}, failed to send invalid card action notice: ${err}`);
    }
    return null;
  }

  if (message.msgid) {
    const operatorName = message.user?.name || message.sender?.name || operatorAccount;
    modifyInteractiveTuituiMsg(
      account,
      chatId,
      chatType,
      message.msgid,
      buildTuituiInteractiveMessage(buildStatusCard({
        originalTitle: result.title,
        submittedText: result.fields ? undefined : (message.action?.[0]?.text || result.action),
        fieldsData: result.fields?.byLabel,
        operatorName,
      })),
    ).catch((err) => {
      log?.warn?.(`[${CHANNEL_ID}] AccountId: ${accountId}, failed to patch card status: ${err}`);
    });
  }

  log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, card action command: ${result.command}`);
  return result.command;
}
